import type { AxiosInstance } from 'axios';
import { addDays, formatISO } from 'date-fns';

import { PaginatedResult, TripRepository } from '@src/services/api/trips/trip.repository';
import type { LinkedTrips, Trip, TripListItem } from '@src/types/api/models';

export class TripRepositoryMock implements TripRepository {
  constructor(private readonly axiosInstance: AxiosInstance) {}

  async getAllTrips(): Promise<TripListItem[]> {
    return [];
  }

  async getTrips(): Promise<PaginatedResult<TripListItem>> {
    return {
      data: [],
      limit: 1000,
      offset: 0,
      count: 0,
      total: 0,
      hasMore: false,
    };
  }

  async getCurrentTrip(): Promise<Trip | null> {
    const { getReservationFixture } = await import('@src/mocks/__fixtures__');
    const possibleTripId = this.getToken();

    // If the token is not a tripId, getReservationFixture will still return a default trip
    return getReservationFixture(possibleTripId).value;
  }

  async getTripById({ tripId }: { tripId: string; locale: string }): Promise<Trip | null> {
    const { getReservationFixture } = await import('@src/mocks/__fixtures__');

    return getReservationFixture(tripId).value;
  }

  async getLinkedTrips(): Promise<LinkedTrips> {
    const { onGoingReservation } = await import('@src/mocks/__fixtures__/reservations/ongoing-reservation');

    return {
      previous: undefined,
      next: {
        checkIn: {
          date: onGoingReservation.checkOut.date,
        },
        checkOut: {
          date: formatISO(addDays(new Date(onGoingReservation.checkOut.date), 2)),
        },
        id: '',
        property: {
          name: 'Nook',
          timezone: 'Europe/Berlin',
          location: {
            id: 'some-loc',
            address: {
              city: 'Berlin',
              countryCode: 'DE',
              postalCode: '10118',
              addressLine1: 'Jugerstr. 12',
            },
          },
        },
        unitGroup: {
          name: 'Intimate',
          images: [
            {
              src: 'https://www.datocms-assets.com/49893/1660219255-d9ccbce4-b124-4f41-bff8-bfb15872b064.jpeg',
              description: '',
            },
          ],
        },
        url: 'https://trip.dev.numastays.com?key=blabla',
      },
    };
  }

  async linkTripToGuest({ reservationIdOrCode }: { reservationIdOrCode: string; lastName: string }): Promise<Trip> {
    const { getReservationFixture } = await import('@src/mocks/__fixtures__');

    return getReservationFixture(reservationIdOrCode).value;
  }

  private getToken(): string | undefined {
    const { Authorization } = this.axiosInstance.defaults.headers;

    if (typeof Authorization !== 'string') {
      return undefined;
    }

    return Authorization.split('Bearer ')[1];
  }
}
